'use client'
import { TLanguages } from 'core/config/locale'
import { memo } from 'react'
import { ContractSocketProvider } from 'core/websocket/contract'
import { TradeSocketProvider } from 'core/websocket/trade'
import LoginProvider from '../LoginProvider'
import hcAxios from 'core/utils/hcAxios'
import { NotificationHolder, PCErrorNotifier } from '@/components/notification'
import useApiEndpointSwitcher from 'core/hooks/InitApp/useApiEndpointSwitcher'
import useDebugButton from 'core/hooks/InitApp/useDebugButton'
import useGA4UserAndDeviceTracking from 'core/hooks/InitApp/useGA4UserAndDeviceTracking'
import useAxiosInitializer from 'core/hooks/InitApp/useAxiosInitializer'
import useSyncLocaleWithURL from './useSyncLocaleWithURL'
import { AntdConfigProvider } from './AntdConfigProvider'
import { Client, Platform } from 'core/hooks/InitApp/handleDefaultQueries'
import useSyncThemeWithQuery from './useSyncThemeWithQuery'
import { isInIOS, isInAndroid } from 'core/utils/appBridge'
import { isMobile } from 'core/utils/getDeviceId'
import { usePxtorem } from '@/hooks/usePxtorem'

/**
 * InitApp 组件 - 应用程序初始化和全局配置组件
 *
 * @component
 * @param {Object} props - 组件属性
 * @param {React.ReactNode} props.children - 子组件
 * @param {Object} props.params - 路由参数
 * @param {TLanguages} props.params.lang - 语言设置
 * @param {'light' | 'dark'} props.params.theme - 主题设置
 *
 * @description
 * 该组件负责：
 * 1. 初始化全局配置（主题、语言等）
 * 2. 设置 WebSocket 连接
 * 3. 配置用户认证
 * 4. 初始化全局服务（axios、调试工具等）
 */
const InitApp = ({
  children,
  params,
}: Readonly<{
  children: React.ReactNode
  params: { lang: TLanguages; theme: 'light' | 'dark' }
}>) => {
  const { lang } = params

  usePxtorem()

  // 同步本地持久化存储的多语言设置与URL中的多语言参数
  useSyncLocaleWithURL(lang)

  // 检查并切换可用接口 API 线路
  useApiEndpointSwitcher()

  // 添加调试按钮
  useDebugButton()

  // 初始化 axios 配置
  useAxiosInitializer({
    errorNotifier: new PCErrorNotifier(),
    platform: isInIOS() ? Platform.IOS : isInAndroid() ? Platform.Android : isMobile() ? Platform.H5 : Platform.Web,
    client: isInIOS() ? Client.IOS : isInAndroid() ? Client.Android : isMobile() ? Client.H5 : Client.Web,
    hcAxios,
    lang,
  })

  // 同步主题设置
  useSyncThemeWithQuery()

  // 初始化 Google Analytics
  useGA4UserAndDeviceTracking()

  return (
    <>
      <AntdConfigProvider lang={lang}>
        <TradeSocketProvider>
          <ContractSocketProvider>
            <LoginProvider>{children}</LoginProvider>
          </ContractSocketProvider>
        </TradeSocketProvider>
      </AntdConfigProvider>
      <NotificationHolder />
    </>
  )
}

export default memo(InitApp)
